import React, { useCallback, useEffect, useState } from 'react'
import message from 'antd/lib/message'
import { apiManager } from 'helpers'

import { CloseIcon, PrimaryButton } from 'components'
import Modal from 'components/AuthModal/components/Modal'
import Overlay from 'components/AuthModal/components/Overlay'
import { OVERLAY } from 'components/AuthModal/styles'
import ProfileLabel from 'components/Profile/ProfileLabel'
import ProfileSelect from 'components/Profile/ProfileSelect'
import ProfileInput from 'components/Profile/ProfileInput'
import ProfileTextarea from 'components/Profile/ProfileTextarea'

const units = [
  {
    label: 'Weeks',
    value: 'weeks',
  },
  {
    label: 'Months',
    value: 'months',
  },
]

const defaultInput = {
  value: '',
  unit: 'weeks',
  reason: '',
}

const FreeSubscriptionModal = ({ show, onClose, userId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [input, setInput] = useState(defaultInput)

  useEffect(() => {
    setInput(defaultInput)
  }, [show])

  const handleInputChange = useCallback(e => {
    const { name, value } = e.target

    setInput(prev => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      message.loading('submitting...', 0)
      setIsSubmitting(true)

      const showError = errorMessage => {
        message.destroy()
        message.error(errorMessage || 'Please try again')
      }

      try {
        const { data } = await apiManager.adminAddFreeSubscription({
          data: {
            userIds: [userId],
            duration: `${input.value} ${input.unit}`,
            reason: input.reason,
          },
        })

        if (!data[0].success) {
          return showError('Unable to add free subscription')
        }

        message.destroy()
        message.success('Free subscription added successfully', 2.5)
        onClose(true)
      } catch (error) {
        showError(error.message)
      } finally {
        setIsSubmitting(false)
      }
    },
    [userId, input, onClose]
  )

  return (
    <Overlay show={show} className={OVERLAY} preventEscBackdropClose={true} onClose={onClose}>
      <Modal innerClassName="bg-white rounded-t-3xl overflow-y-scroll md:rounded-xl">
        <div className="relative w-full p-5 md:p-7 md:py-5 md:w-108">
          <button className="bg-transparent absolute top-3 right-4" onClick={onClose}>
            <CloseIcon className="w-3 h-3" />
          </button>
          <div className="text-center text-xl font-bold font-ownersWide mb-10 underline">
            Add Free Months
          </div>
          <form onSubmit={handleSubmit}>
            <div className="text-center">
              <div className="mb-6">
                <ProfileLabel className="text-black font-semibold">Input</ProfileLabel>
                <ProfileInput
                  showRequired
                  type="number"
                  name="value"
                  min={1}
                  max={12}
                  placeholder="Input"
                  value={input.value}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-6">
                <ProfileLabel className="text-black font-semibold">Unit</ProfileLabel>
                <ProfileSelect
                  name="unit"
                  showDefault={false}
                  options={units}
                  value={input.unit}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <ProfileLabel className="text-black font-semibold">Reason</ProfileLabel>
                <ProfileTextarea
                  name="reason"
                  required={true}
                  value={input.reason}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="flex justify-center mt-14">
              <PrimaryButton
                disabled={!input.value || !input.reason}
                type="submit"
                loading={isSubmitting}
                className="text-base h-10"
              >
                Submit
              </PrimaryButton>
            </div>
          </form>
        </div>
      </Modal>
    </Overlay>
  )
}

export default FreeSubscriptionModal
